/* Hero Video Slider — Luminance Sweep (poster-based, 2D-canvas, decoupled)
   Preloads each slide's poster (or Shopify video thumbnail) — both CORS-safe images,
   so no crossorigin on the videos is needed. On slide change it dissolves the outgoing
   slide's poster away by sweeping a brightness threshold (with a glowing edge) to reveal
   the already-committed next slide underneath. No WebGL, no video pixel reads, never
   blocks the slider. Falls back to the CSS cross-fade if a poster isn't available. */
(function () {
  "use strict";

  var MAXW = 480;
  var cache = (typeof WeakMap !== "undefined") ? new WeakMap() : null;

  var DEBUG = /[?&]lumadebug=1/.test(location.search);
  var dbgBox = null, dbgN = 0;
  function dbg(msg) {
    if (!DEBUG) return;
    if (!dbgBox) {
      dbgBox = document.createElement("div");
      dbgBox.style.cssText = "position:fixed;left:8px;bottom:8px;z-index:99999;max-width:46vw;" +
        "background:rgba(0,0,0,.82);color:#9f9;font:12px/1.4 monospace;padding:8px 10px;" +
        "border-radius:8px;white-space:pre-wrap;pointer-events:none;";
      (document.body || document.documentElement).appendChild(dbgBox);
    }
    dbgN++;
    dbgBox.textContent = ("[" + dbgN + "] " + msg + "\n" + dbgBox.textContent).split("\n").slice(0, 12).join("\n");
  }

  function isMobile() {
    return (window.matchMedia && (window.matchMedia("(max-width: 749px)").matches ||
            window.matchMedia("(pointer: coarse)").matches));
  }
  function reducedMotion() {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }
  function hexRGB(col) {
    col = (col || "#ffffff").trim();
    if (col.charAt(0) === "#") {
      var h = col.slice(1);
      if (h.length === 3) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2];
      var n = parseInt(h, 16);
      return [(n>>16)&255, (n>>8)&255, n&255];
    }
    return [255, 255, 255];
  }

  function buildFromImage(img) {
    var sw = img.naturalWidth, sh = img.naturalHeight;
    if (!sw || !sh) return null;
    var scale = Math.min(1, MAXW / sw);
    var w = Math.max(2, Math.round(sw * scale));
    var h = Math.max(2, Math.round(sh * scale));
    var c = document.createElement("canvas"); c.width = w; c.height = h;
    var ctx = c.getContext("2d");
    var data;
    try { ctx.drawImage(img, 0, 0, w, h); data = ctx.getImageData(0, 0, w, h); }
    catch (e) { return null; }                 // tainted (shouldn't happen for Shopify imgs)
    var d = data.data, lum = new Float32Array(w * h);
    for (var i = 0, p = 0; i < d.length; i += 4, p++) {
      lum[p] = (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) / 255;
    }
    return { img: data, lum: lum, w: w, h: h };
  }

  function buildFromVideo(video) {
    var sw = video.videoWidth, sh = video.videoHeight;
    if (!sw || !sh || video.readyState < 2) return null;
    var scale = Math.min(1, MAXW / sw);
    var w = Math.max(2, Math.round(sw * scale));
    var h = Math.max(2, Math.round(sh * scale));
    var c = document.createElement("canvas"); c.width = w; c.height = h;
    var ctx = c.getContext("2d");
    var data;
    try { ctx.drawImage(video, 0, 0, w, h); data = ctx.getImageData(0, 0, w, h); }
    catch (e) { return null; }
    var d = data.data, lum = new Float32Array(w * h);
    for (var i = 0, p = 0; i < d.length; i += 4, p++) {
      lum[p] = (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) / 255;
    }
    return { img: data, lum: lum, w: w, h: h };
  }

  function snapshotLive(slide) {
    if (reducedMotion() || isMobile() || !slide) return null;
    var v = slide.querySelector("video");
    if (!v) { dbg("live: no video el"); return null; }
    var s = buildFromVideo(v);
    dbg("live snapshot: " + (s ? ("OK " + s.w + "x" + s.h + " (video frame)") : "NULL -> poster"));
    return s;
  }

  function preload() {
    if (!cache) return;
    var slides = document.querySelectorAll("[data-hvs-slide][data-hvs-poster]");
    for (var i = 0; i < slides.length; i++) {
      (function (slide) {
        var url = slide.getAttribute("data-hvs-poster");
        if (!url) return;
        var img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = function () {
          var s = buildFromImage(img);
          if (s) { cache.set(slide, s); dbg("poster cached " + s.w + "x" + s.h); }
          else dbg("poster READ FAILED (tainted?) " + url.slice(0, 40));
        };
        img.onerror = function () { dbg("poster LOAD ERROR " + url.slice(0, 40)); };
        img.src = url;
      })(slides[i]);
    }
  }

  function snapshot(slide) {
    if (reducedMotion()) { dbg("snapshot: skipped (reduced-motion)"); return null; }
    if (isMobile()) { dbg("snapshot: skipped (mobile)"); return null; }
    if (!cache) { dbg("snapshot: no WeakMap"); return null; }
    var s = cache.get(slide) || null;
    dbg("snapshot: " + (s ? "OK (poster ready)" : "NULL (poster not cached -> fade)"));
    return s;
  }

  function dissolve(el, snap, opts) {
    if (!snap) { dbg("dissolve: no snap"); return; }
    dbg("dissolve: START dur=" + (opts.durationMs) + " dir=" + opts.direction);
    var prev = el.querySelector(".hvs-luma-overlay");
    if (prev && prev.parentNode) prev.parentNode.removeChild(prev);

    var dur  = Math.min(6000, Math.max(300, opts.durationMs || 900)); // clamp: never freeze
    var dir  = (opts.direction === "dark") ? -1 : 1;
    var soft = Math.max(0.01, opts.soft != null ? opts.soft : 0.12);
    var band = Math.max(0.01, opts.band != null ? opts.band : 0.06);
    var glow = (opts.glow != null ? opts.glow : 1.2);
    var gc   = hexRGB(opts.glowColor);
    // Optional pink-overlay tint: {r,g,b,a}. Applied source-atop so it only tints the
    // sweep's own (outgoing) pixels; the revealed area shows the next slide's real overlay.
    var tint = (opts.tint && opts.tint.a > 0) ? opts.tint : null;
    var tintStr = tint ? ("rgba(" + tint.r + "," + tint.g + "," + tint.b + "," + tint.a + ")") : null;

    var rect = (opts.mount || el).getBoundingClientRect();
    var dpr = Math.min(1.5, window.devicePixelRatio || 1);
    var ov = document.createElement("canvas");
    ov.className = "hvs-luma-overlay";
    // Mount inside the active slide (z-index 1, under the slide's pink overlay at z-index 2)
    // when a mount target is given; otherwise sit above the whole slider (z-index 5).
    var zIndex = opts.mount ? 1 : 5;
    ov.style.cssText = "position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:" + zIndex + ";";
    ov.width = Math.max(2, Math.round(rect.width * dpr));
    ov.height = Math.max(2, Math.round(rect.height * dpr));
    (opts.mount || el).appendChild(ov);
    var octx = ov.getContext("2d");

    var w = snap.w, h = snap.h, src = snap.img.data, lum = snap.lum;
    // Live mode: re-sample the outgoing video each frame instead of a still snapshot.
    var live = opts.liveVideo || null;
    var sctx = null, liveStalls = 0, lastFrameT = 0;
    if (live) {
      lum = Float32Array.from(snap.lum);             // copy so we never mutate a cached poster
      var sample = document.createElement("canvas"); sample.width = w; sample.height = h;
      sctx = sample.getContext("2d", { willReadFrequently: true });
    }
    var work = document.createElement("canvas"); work.width = w; work.height = h;
    var wctx = work.getContext("2d");
    var out = wctx.createImageData(w, h), od = out.data;

    var ca = ov.width / ov.height, ra = w / h, dw, dh, dx, dy;
    if (ca > ra) { dw = ov.width; dh = ov.width / ra; }
    else { dh = ov.height; dw = ov.height * ra; }
    dx = (ov.width - dw) / 2; dy = (ov.height - dh) / 2;

    var done = false, raf = null, safety = null;
    function cleanup() {
      if (done) return; done = true;
      try { cancelAnimationFrame(raf); } catch (e) {}
      try { clearTimeout(safety); } catch (e) {}
      if (live) { try { live.pause(); } catch (e) {} }   // stop the deferred outgoing video
      if (ov.parentNode) ov.parentNode.removeChild(ov);
    }

    var t0 = performance.now();
    function frame(now) {
      if (done) return;
      var k = Math.min(1, (now - t0) / dur);

      // LIVE: re-grab the outgoing video frame this tick (downscaled), recompute luminance.
      if (live && sctx) {
        var dt = lastFrameT ? (now - lastFrameT) : 16;
        lastFrameT = now;
        if (dt > 55) { if (++liveStalls >= 2) { live = null; } }   // weak device -> freeze to last frame
        if (live && live.readyState >= 2 && live.videoWidth) {
          try {
            sctx.drawImage(live, 0, 0, w, h);
            var sd = sctx.getImageData(0, 0, w, h).data;
            src = sd;
            for (var sp = 0, sq = 0; sp < lum.length; sp++, sq += 4) {
              lum[sp] = (0.299 * sd[sq] + 0.587 * sd[sq + 1] + 0.114 * sd[sq + 2]) / 255;
            }
          } catch (e) { live = null; }                              // taint/error -> last frame
        }
      }

      // steady (linear) sweep; tight range that still fully clears at the ends
      var hi = 1 + soft, lo = -soft;
      var thresh = (dir > 0) ? (hi - k * (hi - lo)) : (lo + k * (hi - lo));
      var inv2soft = 1 / (2 * soft);

      for (var p = 0, q = 0; p < lum.length; p++, q += 4) {
        var L = lum[p];
        var rev = (dir > 0) ? (L - (thresh - soft)) * inv2soft
                            : ((thresh + soft) - L) * inv2soft;
        rev = rev < 0 ? 0 : rev > 1 ? 1 : rev;
        var oldA = 1 - rev;
        var dd = Math.abs(L - thresh) / band;
        var g = dd < 1 ? (1 - dd) * glow : 0;
        if (g > 1) g = 1;
        var A = oldA + g * (1 - oldA);
        if (A > 0.0001) {
          var inv = 1 / A, ga = g * (1 - oldA);
          od[q]     = (src[q]     * oldA + gc[0] * ga) * inv;
          od[q + 1] = (src[q + 1] * oldA + gc[1] * ga) * inv;
          od[q + 2] = (src[q + 2] * oldA + gc[2] * ga) * inv;
          od[q + 3] = A * 255;
        } else { od[q] = od[q + 1] = od[q + 2] = od[q + 3] = 0; }
      }
      wctx.putImageData(out, 0, 0);
      if (tintStr) {                              // tint only the sweep's opaque pixels
        wctx.globalCompositeOperation = "source-atop";
        wctx.fillStyle = tintStr;
        wctx.fillRect(0, 0, w, h);
        wctx.globalCompositeOperation = "source-over";
      }
      octx.clearRect(0, 0, ov.width, ov.height);
      octx.drawImage(work, dx, dy, dw, dh);

      if (k >= 1) { dbg("dissolve: DONE"); cleanup(); return; }
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);
    safety = setTimeout(function () { dbg("dissolve: safety-timeout"); cleanup(); }, dur + 500);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", preload);
  else preload();

  window.HVSLumaSweep = { snapshot: snapshot, snapshotLive: snapshotLive, dissolve: dissolve, preload: preload };
})();
